import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikProps, FormikHelpers, getIn, FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next'; // Import TFunction from i18next core
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import './InputForm.scss'; // Assicurati che esista o rimuovi
import Dialog from '../../../../components/Dialog/Dialog';
import Accordion from '../../../../components/Accordion/Accordion';
import { FieldConfig } from '../../../services/ValidationService';
import { setAccordionToOpen, selectAccordionToOpen, submitSucceeded } from '../../../store/form/formControlSlice';
import { useFormSubmission } from '../../../Dynamic/hooks/useFormSubmission';
import { useFormStateManagement } from '../../../hooks/useFormStateManagement';
import { unit as unitEnum, EProjectType } from '../../../enum/project';
import { useGeoGridFormSetup } from '../../hooks/useGeoGridFormSetup';
import { IGeogridData, IGeogridItem } from '../../../api/IApiResponse';
import { createSafeHtml } from '../../../utils/htmlSanitizer';
import * as Yup from 'yup';
import { Box, CircularProgress, Typography, Button, IconButton, ToggleButton, ToggleButtonGroup, FormLabel } from '@mui/material';
import GeogridLayerManager from './GeogridLayerManager';
import { convertGeoGridData } from '../../../services/UnitConversionService';
import { debounce } from 'lodash-es'; // Still needed for graphic preview functionality
import { setGraphicPreviewData, IGraphicPreviewData } from '../../../Dynamic/store/project/componentiGraficiSlice';
import { RootState } from '../../../../reduxService';
import { setGeoGridData, setGeogridHasBeenRan, setGeogridUserModified } from '../../../Dynamic/store/project/projectslice';
import { GeoGridFormData, GeoGridFormikProps, GeoGridFieldValue, RoundableValue, GeoGridErrorMessage, GeoGridGraphicPayload } from '../../../types/FormTypes';
import { logDebug, logError, logWarn, LogTag } from '../../../utils/utils';
import InfoBoxButton from '../../../components/InfoBoxButton';
import GeoGridFieldInput from './GeoGridFieldInput';

// 🔧 BUG FIX: Internal component to sync form state with FormStateMachine
// This component is rendered inside Formik render props to access dirty/values
// without using formikRef.current in useEffect dependencies (anti-pattern)
const FormStateSyncComponent: React.FC<{
    dirty: boolean;
    values: unknown;
    initialValues: unknown;
    updateFormState: (dirty: boolean, values: unknown, initialValues: unknown) => void;
}> = ({ dirty, values, initialValues, updateFormState }) => {
    useEffect(() => {
        updateFormState(dirty, values, initialValues);
    }, [dirty, values, initialValues, updateFormState]);

    return null;
};

const roundNumbersInObject = (obj: unknown, precision: number): unknown => {
    if (obj === null || typeof obj !== 'object') {
        if (typeof obj === 'number') {
            return parseFloat(obj.toFixed(precision));
        }
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => roundNumbersInObject(item, precision));
    }

    const newObj: Record<string, unknown> = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            newObj[key] = roundNumbersInObject((obj as Record<string, unknown>)[key], precision);
        }
    }
    return newObj;
};
interface RenderSingleFieldProps {
    fullFieldName: string;
    fieldConfig: FieldConfig;
    t: TFunction;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    // handler opzionale per casi speciali (come il cambio metodo in DynamicForm)
    // Non sembra necessario per GeoGridForm, quindi può essere omesso quando si chiama
    handleInputChange?: (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        setFieldValue: (field: string, value: unknown) => void,
        fieldName: string // Nome semplice del campo (non annidato)
    ) => void;
    // setFieldValue è necessario solo se handleInputChange viene usato
    setFieldValue?: (field: string, value: unknown) => void;
    // Aggiungi altre props se necessario (es. isDisabled)
}

// Funzione helper per renderizzare il messaggio di errore di Formik
// (Prende la versione migliorata che gestisce diversi tipi di msg)
const renderErrorMessageComponent = (msg: GeoGridErrorMessage | string | unknown) => {
    let messageToRender: React.ReactNode = null;
    if (typeof msg === 'string') {
        messageToRender = msg;
    } else if (msg && typeof msg === 'object') {
        // Gestisce oggetti e array (che sono oggetti)
        // Prova a estrarre un messaggio o mostra JSON (potresti affinare questo)
        logWarn('GeoGridForm', 'Received non-string error message', msg, LogTag.VALIDATION);
        try {
            // Attempt to stringify, handle potential circular references? (unlikely here)
            messageToRender = JSON.stringify(msg);
        } catch (e) {
            logError('GeoGridForm', 'Failed to stringify error message object', e, LogTag.ERROR_HANDLING);
            messageToRender = '[Unrenderable Error Object]';
        }
    }

    return messageToRender ? (
        <div className="error formik-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {messageToRender}
        </div>
    ) : null;
};

// La funzione helper riutilizzabile
export const renderSingleField = ({ fullFieldName, fieldConfig, t, getMeasurementUnit, handleInputChange, setFieldValue }: RenderSingleFieldProps): React.ReactNode => {
    if (!fieldConfig) {
        return null;
    }

    const isDisabled = false; // Logica di disabilitazione se necessaria

    return (
        <div key={fullFieldName} className="field-container" data-testid={`field-${fullFieldName}`}>
            <label htmlFor={fullFieldName} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>
                    {t(fieldConfig.label || '', {
                        // Aggiungi contesto o altri parametri per t() se necessario
                        unit_measure: getMeasurementUnit(Array.isArray(fieldConfig.unit) ? fieldConfig.unit : undefined),
                    })}
                </span>
                {fieldConfig.infoBox && (
                    <InfoBoxButton
                        textContent={t(fieldConfig.infoBox)}
                        tooltipText={t(`${fieldConfig.name}_info_tooltip`, {}) || t(fieldConfig.infoBox)}
                        dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || t(fieldConfig.label || '')}
                        size="small"
                    />
                )}
                {fieldConfig.infoBoxImgSrc && (
                    <InfoBoxButton
                        imageSrc={fieldConfig.infoBoxImgSrc}
                        tooltipText={t(`${fieldConfig.name}_info_tooltip`, {}) || undefined}
                        dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || fieldConfig.label}
                        imageAlt={t(`${fieldConfig.name}_info_alt`, {}) || `${fieldConfig.label} information`}
                        size="small"
                    />
                )}
            </label>

            {fieldConfig.renderAs === 'select' ? (
                <Field
                    as="select"
                    id={fullFieldName}
                    name={fullFieldName}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        // Always explicitly call setFieldValue to ensure nested field names work correctly
                        // Formik's default behavior may not properly handle nested names
                        if (setFieldValue) {
                            setFieldValue(fullFieldName, e.target.value);
                        }
                        // Call custom handler if provided
                        if (handleInputChange && setFieldValue) {
                            handleInputChange(e, setFieldValue, fieldConfig.name);
                        }
                    }}
                    disabled={isDisabled}>
                    {/* Opzione vuota di default per select non obbligatorie? */}
                    {/* {!fieldConfig.required && <option value="">{t('select_placeholder', '-- Select --')}</option>} */}
                    {fieldConfig.options?.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Field>
            ) : (
                <Field name={fullFieldName}>{({ field }: { field: any }) => <GeoGridFieldInput field={field} fieldConfig={fieldConfig} fieldName={fullFieldName} disabled={isDisabled} />}</Field>
            )}
            {/* Usa il componente ErrorMessage di Formik */}
            <ErrorMessage name={fullFieldName} render={renderErrorMessageComponent} />
        </div>
    );
};

interface FieldGroupProps {
    groupName: string;
    values: GeoGridFormData;
    dynamicFields: FieldConfig[];
    setFieldValue: (field: string, value: unknown) => void;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: unknown) => void, fieldName: string) => void; // Optional
    getMeasurementUnit: (unit: string[] | undefined) => string;
    t: TFunction;
    isNewProject: boolean;
}
export const FieldGroup: React.FC<FieldGroupProps> = ({ groupName, values, dynamicFields, setFieldValue, handleInputChange, getMeasurementUnit, t, isNewProject }) => {
    const isSubgradeGroup = groupName === 'subgrade';
    const selectedSubgradeMethod = isSubgradeGroup ? getIn(values, `${groupName}.subgradeInputMethod`) : null;
    const fieldsToRender = dynamicFields.filter(fieldConfig => {
        if (!fieldConfig?.name) return false; // Skip invalid configs
        if (fieldConfig.displayCondition) {
            // The field path in displayCondition (e.g., "type.geogridType") is relative to the root of Formik values.
            const controllingFieldValue = getIn(values, fieldConfig.displayCondition.field);
            if (controllingFieldValue !== fieldConfig.displayCondition.value) {
                return false; // Hide if condition not met
            }
        }

        // Handle hideWhen condition - hide field if condition matches
        if (fieldConfig.hideWhen) {
            const controllingFieldValue = getIn(values, fieldConfig.hideWhen.field);
            if (controllingFieldValue === fieldConfig.hideWhen.value) {
                return false; // Hide if hideWhen condition is met
            }
        }

        if (!isSubgradeGroup) {
            return true; // Render all fields if not the subgrade group
        }
        //logDebug('GeoGridForm FieldGroup', `Rendering field: ${fieldConfig.name} in group: ${groupName}`, undefined, LogTag.UI_INTERACTIONS);

        if (fieldConfig.name === 'subgradeInputMethod') return true;

        if (selectedSubgradeMethod === 'cbr') {
            // Se il metodo è CBR, mostra 'cbrSubgrade' e nascondi 'deformationModulus'
            if (fieldConfig.name === 'deformationModulus') return false;
            // if (fieldConfig.name === 'cbrSubgrade') return true; // Già incluso nel return true di default per il gruppo
        } else if (selectedSubgradeMethod === 'modulus') {
            // Se il metodo è Modulus, mostra 'deformationModulus' e nascondi 'cbrSubgrade'
            if (fieldConfig.name === 'cbrSubgrade') return false;
            // if (fieldConfig.name === 'deformationModulus') return true; // Già incluso nel return true di default per il gruppo
        } else {
            // Se nessun metodo è selezionato (o un valore imprevisto),
            // nascondi entrambi i campi condizionali.
            if (fieldConfig.name === 'cbrSubgrade' || fieldConfig.name === 'deformationModulus') return false;
        }
        return true;
    });
    const renderField = (fieldName: string, fieldConfig: FieldConfig | undefined, index: number) => {
        if (!fieldConfig) return null;
        const nestedFieldName = `${groupName}.${fieldName}`;
        // fieldValue non è più passato qui, Formik <Field> lo gestisce con 'name'

        // const safeFieldValue = fieldValue === undefined ? (fieldConfig.type === 'number' ? 0 : '') : fieldValue; // Non più necessario
        const isDisabled = false;

        if (fieldConfig.renderAs === 'toggleButtonGroup') {
            return (
                <div key={nestedFieldName} className="field-container toggle-button-container" data-testid={`field-${nestedFieldName}`} style={{ marginBottom: '16px' }}>
                    <FormLabel component="legend" sx={{ mb: 1, display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {t(fieldConfig.label || '')}
                    </FormLabel>
                    <Field name={nestedFieldName}>
                        {({ field, form }: { field: GeoGridFieldValue; form: GeoGridFormikProps }) => (
                            <ToggleButtonGroup
                                color="primary"
                                value={field.value}
                                exclusive
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        form.setFieldValue(nestedFieldName, newValue);

                                        if (fieldName === 'subgradeInputMethod') {
                                            // Determina i valori di default dai dynamicFields originali
                                            const cbrConfig = dynamicFields.find(f => f.name === 'cbrSubgrade');
                                            const modulusConfig = dynamicFields.find(f => f.name === 'deformationModulus');

                                            if (newValue === 'cbr') {
                                                // Resetta deformationModulus al suo defaultValue o stringa vuota
                                                form.setFieldValue(`${groupName}.deformationModulus`, modulusConfig?.defaultValue ?? '');
                                            } else if (newValue === 'modulus') {
                                                // Resetta cbrSubgrade al suo defaultValue o stringa vuota
                                                form.setFieldValue(`${groupName}.cbrSubgrade`, cbrConfig?.defaultValue ?? '');
                                            }
                                        }
                                    }
                                }}
                                aria-label={t(fieldConfig.label || fieldConfig.name) ?? undefined}
                                size="small">
                                {fieldConfig.options?.map(option => (
                                    <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', px: 2 }}>
                                        {t(option.label)}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        )}
                    </Field>
                    <ErrorMessage name={nestedFieldName} render={renderErrorMessage} />
                </div>
            );
        } // --- FINE ToggleButtonGroup ---
        // Use the index in the key to guarantee uniqueness even if fieldName is duplicated within the group
        return (
            <div key={`${nestedFieldName}-${index}`} className="field-container" data-testid={`field-${nestedFieldName}`}>
                <label htmlFor={nestedFieldName} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>
                        {t(fieldConfig.label || '', {
                            unit_measure: getMeasurementUnit(Array.isArray(fieldConfig.unit) ? fieldConfig.unit : undefined),
                        })}
                    </span>
                    {fieldConfig.infoBox && <InfoBoxButton textContent={fieldConfig.infoBox} tooltipText={fieldConfig.infoBox} dialogTitle={fieldConfig.label || fieldConfig.name} size="small" />}
                    {fieldConfig.infoBoxImgSrc && (
                        <InfoBoxButton
                            imageSrc={fieldConfig.infoBoxImgSrc}
                            tooltipText={t(`${fieldConfig.name}_info_tooltip`, {}) || undefined}
                            dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || fieldConfig.label}
                            imageAlt={t(`${fieldConfig.name}_info_alt`, {}) || `${fieldConfig.label} information`}
                            size="small"
                        />
                    )}
                </label>
                {fieldConfig.renderAs === 'select' ? (
                    <Field
                        as="select"
                        id={nestedFieldName}
                        name={nestedFieldName}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            // Always explicitly call setFieldValue to ensure nested field names work correctly
                            setFieldValue(nestedFieldName, e.target.value);
                            // Call custom handler if provided
                            if (handleInputChange) {
                                handleInputChange(e, setFieldValue, fieldName);
                            }
                        }}
                        disabled={isDisabled}>
                        {fieldConfig.options?.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Field>
                ) : (
                    <Field name={nestedFieldName}>
                        {({ field }: { field: any }) => <GeoGridFieldInput field={field} fieldConfig={fieldConfig} fieldName={nestedFieldName} disabled={isDisabled} />}
                    </Field>
                )}
                <ErrorMessage name={nestedFieldName} render={renderErrorMessage} />
            </div>
        );
    };
    return (
        <>
            {fieldsToRender.map((fieldConfig, index) => {
                const fieldName = fieldConfig.name;
                //const fieldValue = groupFields ? groupFields[fieldName] : undefined;
                // Pass index as the fourth argument
                return renderField(fieldName, fieldConfig, index);
            })}
        </>
    );
};
export const StyledErrorMessage: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <div className="error formik-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
        {children}
    </div>
);
const renderErrorMessage = (msg: string) => (
    <div className="error formik-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
        <span dangerouslySetInnerHTML={createSafeHtml(msg)} />
    </div>
);
// --- Fine Componenti interni ---

// --- Componente GeoGridForm ---
/**
 * Layers for the drawing: Formik holds a height still being typed (empty field, unfinished or
 * unusable text) as a string, so only a finite number reaches the preview; the layer itself stays.
 */
const toPreviewGeogrids = (geogrids: Array<Record<string, unknown>> | undefined): IGeogridItem[] | undefined =>
    geogrids?.map(geogrid => {
        const { position, ...otherFields } = geogrid;
        return { ...(otherFields as IGeogridItem), position: typeof position === 'number' && Number.isFinite(position) ? position : undefined };
    });

export const useGraphicPreview = (values: GeoGridFormData, projectUnit: unitEnum, groupedFields: Record<string, FieldConfig[]>, dispatch: ReturnType<typeof useAppDispatch>) => {
    const lastPayloadRef = useRef<string | null>(null);
    const isMountedRef = useRef(true);

    const debouncedUpdateGraphic = useMemo(
        () =>
            debounce((payload: IGraphicPreviewData) => {
                if (!isMountedRef.current) return; // Prevent updates after unmount

                const payloadString = JSON.stringify(roundNumbersInObject(payload, 6));
                if (payloadString !== lastPayloadRef.current) {
                    logDebug('GeoGridForm useGraphicPreview', 'Dispatching graphic update', payload, LogTag.CHARTS);
                    dispatch(setGraphicPreviewData(payload));
                    lastPayloadRef.current = payloadString;
                }
            }, 300),
        [dispatch]
    );

    // Stable references for complex nested values to prevent unnecessary effect runs
    const stableGeogridType = useMemo(() => values.type?.geogridType, [values.type?.geogridType]);
    const stableSurfaceLayerData = useMemo(
        () => ({
            thickness: values.surfaceLayer?.thickness,
        }),
        [values.surfaceLayer?.thickness]
    );
    const stableBaseCourseData = useMemo(
        () => ({
            thickness: values.geogridBaseCourse?.thickness,
            geogrids: values.geogridBaseCourse?.geogrids,
        }),
        [values.geogridBaseCourse?.thickness, values.geogridBaseCourse?.geogrids]
    );
    const stableSubBaseCourseData = useMemo(
        () => ({
            thickness: values.geogridSubBaseCourse?.thickness,
            geogrids: values.geogridSubBaseCourse?.geogrids,
        }),
        [values.geogridSubBaseCourse?.thickness, values.geogridSubBaseCourse?.geogrids]
    );

    useEffect(() => {
        if (!stableGeogridType) return;

        const graphicPayload: IGraphicPreviewData = {
            type: { geogridType: stableGeogridType },
            surfaceLayer:
                stableSurfaceLayerData.thickness !== undefined
                    ? {
                          thickness: typeof stableSurfaceLayerData.thickness === 'string' ? parseFloat(stableSurfaceLayerData.thickness) || undefined : stableSurfaceLayerData.thickness,
                      }
                    : undefined,
            geogridBaseCourse:
                stableBaseCourseData.thickness !== undefined || stableBaseCourseData.geogrids
                    ? {
                          thickness: typeof stableBaseCourseData.thickness === 'string' ? parseFloat(stableBaseCourseData.thickness) || undefined : stableBaseCourseData.thickness,
                          geogrids: toPreviewGeogrids(stableBaseCourseData.geogrids),
                      }
                    : undefined,
            geogridSubBaseCourse:
                stableSubBaseCourseData.thickness !== undefined || stableSubBaseCourseData.geogrids
                    ? {
                          thickness: typeof stableSubBaseCourseData.thickness === 'string' ? parseFloat(stableSubBaseCourseData.thickness) || undefined : stableSubBaseCourseData.thickness,
                          geogrids: toPreviewGeogrids(stableSubBaseCourseData.geogrids),
                      }
                    : undefined,
        };

        let finalPayload = graphicPayload;

        // Convert to SI if needed
        if (projectUnit === unitEnum.Imperial) {
            const converted = convertGeoGridData(finalPayload as any, groupedFields, unitEnum.SI);
            finalPayload = (converted as IGraphicPreviewData) || finalPayload;
        }

        if (finalPayload.surfaceLayer || finalPayload.geogridBaseCourse || finalPayload.geogridSubBaseCourse) {
            debouncedUpdateGraphic(finalPayload);
        }

        return () => {
            debouncedUpdateGraphic.cancel();
        };
    }, [stableGeogridType, stableSurfaceLayerData, stableBaseCourseData, stableSubBaseCourseData, projectUnit, groupedFields, debouncedUpdateGraphic]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            debouncedUpdateGraphic.cancel();
        };
    }, [debouncedUpdateGraphic]);
};

const GeoGridForm: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const formikRef = useRef<FormikProps<any>>(null);
    const lastDispatchedGeoGridPayloadRef = useRef<string | null>(null); // Per confrontare il payload

    const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({});
    const accordionsInitializedRef = useRef(false); // Ref to track if default accordion states are set
    const accordionToOpen = useAppSelector(selectAccordionToOpen);

    // Recupera i dati del progetto globale, inclusi il tipo e l'unità
    const projectData = useAppSelector((state: RootState) => state.Dynamic.project?.data);
    const currentProjectType = projectData?.type;
    const projectUnit = projectData?.unit ?? unitEnum.SI; // Default a SI se non definito
    // Definizione displayType come in InputForm
    const displayType = currentProjectType || 'N/A';

    const {
        loading,
        errorDialog: setupError,
        groupedFields,
        validationSchema,
        validatedData, // InitialValues
        projectUnitFromState,
        isNewProject,
        productData,
        config,
    } = useGeoGridFormSetup();

    useFormSubmission(formikRef);

    // 🔧 BUG FIX: Integrate form state management to handle geogridHasBeenRan reset
    // CONTEXT: FormStateManagementService dispatches setGeogridHasBeenRan(false) when form becomes FORM_DIRTY
    // This integration ensures GeoGrid form participates in the state machine pattern
    const { updateFormState } = useFormStateManagement();

    const navigate = useNavigate();
    const geogridHasBeenRan = useAppSelector((state: RootState) => state.Dynamic.project?.data?.geogridHasBeenRan);

    // 🔧 BUG FIX: Redirect from /geogrid-results to /geogrid when geogridHasBeenRan becomes false
    // CONTEXT: When user modifies a non-design field on results page, FormStateManagementService
    // dispatches setGeogridHasBeenRan(false) because results are no longer valid.
    // This effect handles the automatic redirect back to the form page.
    useEffect(() => {
        const isOnResultsPage = location.pathname.includes('/geogrid-results');

        if (isOnResultsPage && geogridHasBeenRan === false) {
            // Extract projectId from URL: /geogrid-results/:id
            const pathParts = location.pathname.split('/');
            const projectId = pathParts[pathParts.length - 1];

            if (projectId && projectId !== 'geogrid-results') {
                logDebug('GeoGridForm', `🔄 Redirecting from geogrid-results to geogrid for project ${projectId} (geogridHasBeenRan=false)`, undefined, LogTag.NAVIGATION);
                navigate(`/geogrid/${projectId}`);
            }
        }
    }, [geogridHasBeenRan, location.pathname, navigate]);

    const [localErrorDialog, setLocalErrorDialog] = useState<string | null>(null);

    // Determina se il campo geogridType deve essere disabilitato
    const isGeogridTypeDisabled = currentProjectType !== undefined && currentProjectType !== EProjectType.geogrid;

    useEffect(() => {
        setLocalErrorDialog(setupError);
    }, [setupError]);

    useEffect(() => {
        // Initialize default open accordions only once when groupedFields is ready
        if (groupedFields && Object.keys(groupedFields).length > 0 && !accordionsInitializedRef.current) {
            const initialDefaultOpenStates: Record<string, boolean> = {};
            // Iterate over group names that represent actual accordions
            Object.keys(groupedFields)
                .filter(groupName => groupName !== 'geogridItem') // 'geogridItem' holds definitions, not an accordion itself
                .forEach(groupName => {
                    const groupConfig = groupedFields[groupName];
                    // Open by default only if group has exactly one field and is NOT geogridBaseCourse or geogridSubBaseCourse
                    if (Array.isArray(groupConfig) && groupConfig.length === 1 && groupName !== 'geogridBaseCourse' && groupName !== 'geogridSubBaseCourse') {
                        initialDefaultOpenStates[groupName] = true;
                    }
                });

            if (Object.keys(initialDefaultOpenStates).length > 0) {
                setOpenAccordions(prevOpenAccordions => ({
                    ...prevOpenAccordions, // Preserve any states potentially set by other means (e.g., Redux)
                    ...initialDefaultOpenStates, // Apply defaults
                }));
            }
            accordionsInitializedRef.current = true;
        }

        // Cleanup on unmount
        return () => {
            accordionsInitializedRef.current = false;
        };
    }, [groupedFields]);

    useEffect(() => {
        if (accordionToOpen) {
            logDebug('GeoGridForm', `Received request to open accordion: ${accordionToOpen}`, undefined, LogTag.UI_INTERACTIONS);
            setOpenAccordions(prev => ({ ...prev, [accordionToOpen]: true }));
            dispatch(setAccordionToOpen(null));
        }
    }, [accordionToOpen, dispatch]);
    const handleAccordionToggle = useCallback((groupName: string) => {
        setOpenAccordions(prev => {
            const newState = !prev[groupName];
            logDebug('GeoGridForm', `User toggled ${groupName} to ${newState ? 'open' : 'closed'}`, undefined, LogTag.UI_INTERACTIONS);
            return { ...prev, [groupName]: newState };
        });
    }, []);
    const getMeasurementUnit = useCallback(
        (unitArray: string[] | undefined) => {
            if (!unitArray) return '';
            // Usa projectUnit direttamente invece di projectUnitFromState per coerenza
            const index = projectUnit === unitEnum.SI ? 0 : 1;
            if (unitArray?.length > index) {
                return ' (' + unitArray[index] + ')';
            }
            return '';
        },
        [projectUnit] // Stable dependency
    );

    // Handler per il submit di Formik (chiamato DOPO validazione OK da useFormSubmission)
    const handleGeoGridFormikSubmit = (values: GeoGridFormData, formikHelpers: FormikHelpers<GeoGridFormData>) => {
        logDebug('GeoGridForm', 'Formik onSubmit (Validation OK)', values, LogTag.FORM_SUBMISSION);
        // NON aggiornare lo stato GeoGrid qui direttamente.
        // Invia i dati validati allo Stepper tramite l'azione submitSucceeded.
        // Lo Stepper deciderà poi cosa fare (chiamare API Save o API Run GeoGrid).
        dispatch(submitSucceeded(values)); // Passa i dati validati
        //dispatch new values to state.dynamic.project.data.projectInfo.data.geogrid
        dispatch(setGeoGridData(values as IGeogridData));

        // Reset form to prevent race condition with dirty check
        formikHelpers.resetForm({ values });
        formikHelpers.setSubmitting(false);
    };

    // --- Gestione Stati Loading/Error dal Hook ---
    // Mostra prima eventuali errori di setup/validazione per non mascherare i messaggi
    if (localErrorDialog || !validationSchema) {
        return (
            <Dialog
                open={!!localErrorDialog}
                title={t('error_dialog_title') || 'Error'}
                onClose={() => setLocalErrorDialog(null)}
                actions={[{ text: 'OK', callback: () => setLocalErrorDialog(null), color: 'primary' }]}>
                <div>{localErrorDialog || 'Failed to load form configuration.'}</div>
            </Dialog>
        );
    }

    // If still loading, show spinner and loading message
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
                <Typography ml={2}>{'Loading GeoGrid Form...'}</Typography>
            </Box>
        );
    }

    // If product data is missing after loading finished, show a clear message (helps debugging network / config issues)
    if (!productData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={5}>
                <Typography ml={2} sx={{ maxWidth: 600, textAlign: 'center' }}>
                    {t('geogrid_product_data_missing', 'Product data not available. Please check your network connection or project configuration.')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 4, py: 2, maxWidth: 700, margin: '0 auto' }}>
            {/* prendo questa sezione dal layout in InputForm.tsx */}
            <div>
                <div className="form-header">
                    <h3>
                        <span className="fw-bold">{t(displayType)}</span>
                    </h3>
                    <h4>{t('geogrid_input_data')}</h4>
                </div>
            </div>
            {/* Conditionally render the form only when ready */}
            {!loading && productData && validationSchema && validatedData && Object.keys(validatedData).length > 0 ? (
                <>
                    {/* <Typography variant='h6' gutterBottom>
                        GeoGrid Input Data
                    </Typography> */}
                    <Formik
                        innerRef={formikRef}
                        key={`geogrid-${projectData?.guid || 'new'}`} // Stable key based on project GUID
                        initialValues={roundNumbersInObject(validatedData, 2) as typeof validatedData}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        validateOnChange={true}
                        validateOnBlur={true}
                        onSubmit={handleGeoGridFormikSubmit}>
                        {({ setFieldValue, values, dirty }) => (
                            <>
                                <FormStateSyncComponent dirty={dirty} values={values} initialValues={validatedData} updateFormState={updateFormState} />
                                <GeoGridFormContent
                                    setFieldValue={setFieldValue}
                                    values={values as GeoGridFormData}
                                    dirty={dirty}
                                    groupedFields={groupedFields}
                                    projectUnit={projectUnit}
                                    dispatch={dispatch}
                                    location={location}
                                    isGeogridTypeDisabled={isGeogridTypeDisabled}
                                    currentProjectType={currentProjectType}
                                    openAccordions={openAccordions}
                                    handleAccordionToggle={handleAccordionToggle}
                                    getMeasurementUnit={getMeasurementUnit}
                                    isNewProject={isNewProject}
                                    productData={productData}
                                    t={t}
                                    config={config}
                                />
                            </>
                        )}
                    </Formik>
                </>
            ) : (
                // Optional: Render a placeholder or nothing while loading/validating
                // The loading indicator is already handled above, so this might be empty
                // or show a message if data is missing after loading finishes.
                !loading && <Typography>Form data is not available.</Typography>
            )}

            <Dialog open={!!localErrorDialog} title={t('error_dialog_title') || 'Error'} onClose={() => setLocalErrorDialog(null)}>
                <div>{localErrorDialog}</div>
            </Dialog>
        </Box>
    );
};

// Extract accordion rendering logic
interface AccordionSectionProps {
    groupName: string;
    parentFieldConfigs: FieldConfig[];
    itemFieldConfigs?: FieldConfig[];
    isOpen: boolean;
    onToggle: () => void;
    values: GeoGridFormData;
    setFieldValue: (field: string, value: unknown) => void;
    t: TFunction;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    isNewProject: boolean;
    productData: any;
    isGeogridTypeDisabled?: boolean;
    currentProjectType?: EProjectType;
    accordionTitles?: Record<string, string>;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
    groupName,
    parentFieldConfigs,
    itemFieldConfigs,
    isOpen,
    onToggle,
    values,
    setFieldValue,
    t,
    getMeasurementUnit,
    isNewProject,
    productData,
    isGeogridTypeDisabled,
    currentProjectType,
    accordionTitles,
}) => {
    const isGeogridLayerGroup = !!(itemFieldConfigs && (groupName === 'geogridBaseCourse' || groupName === 'geogridSubBaseCourse'));

    // Get the current geogrid type from form values
    const geogridType = getIn(values, 'type.geogridType');

    // Determine the translation key based on geogrid type for Railway-specific labels
    // Use useMemo to ensure title updates when geogridType changes
    const translatedTitle = React.useMemo(() => {
        // Railway-specific titles take precedence (keep backward compatibility)
        if (geogridType === 'Railway' && ['geogridBaseCourse', 'geogridSubBaseCourse', 'baseCourse', 'subBaseCourse'].includes(groupName)) {
            return t(`${groupName}_railway`);
        }
        // Use accordionTitles if available, otherwise fall back to groupName
        const translationKey = accordionTitles?.[groupName] || groupName;
        return t(translationKey);
    }, [groupName, geogridType, t, accordionTitles]);

    if (!parentFieldConfigs || parentFieldConfigs.length === 0) return null;

    return (
        <Accordion title={translatedTitle} isOpen={isOpen} onToggle={onToggle} data-testid={`accordion-${groupName}`}>
            {groupName === 'type' ? (
                <TypeSection groupName={groupName} typeFieldConfig={parentFieldConfigs.find(fc => fc.name === 'geogridType')!} isDisabled={isGeogridTypeDisabled} t={t} />
            ) : isGeogridLayerGroup ? (
                <GeogridLayerSection
                    groupName={groupName}
                    parentFieldConfigs={parentFieldConfigs}
                    itemFieldConfigs={itemFieldConfigs!}
                    t={t}
                    getMeasurementUnit={getMeasurementUnit}
                    productData={productData}
                />
            ) : (
                <FieldGroup
                    groupName={groupName}
                    values={values}
                    dynamicFields={parentFieldConfigs}
                    setFieldValue={setFieldValue}
                    getMeasurementUnit={getMeasurementUnit}
                    t={t}
                    isNewProject={isNewProject}
                />
            )}
        </Accordion>
    );
};

// Extract type section component
const TypeSection: React.FC<{
    groupName: string;
    typeFieldConfig: FieldConfig;
    isDisabled?: boolean;
    t: TFunction;
}> = ({ groupName, typeFieldConfig, isDisabled, t }) => (
    <Box sx={{ padding: '16px' }}>
        <Field name={`${groupName}.${typeFieldConfig.name}`}>
            {({ field, form }: { field: GeoGridFieldValue; form: GeoGridFormikProps }) => (
                <ToggleButtonGroup
                    color="primary"
                    value={field.value}
                    exclusive
                    onChange={(event, newValue) => {
                        if (newValue !== null && !isDisabled) {
                            form.setFieldValue(`${groupName}.${typeFieldConfig.name}`, newValue);

                            // Set default values for fields that become visible due to displayCondition change
                            if (typeFieldConfig.name === 'geogridType') {
                                if (newValue === 'Rectangular') {
                                    // Set defaults for Rectangular load fields if they don't already have values
                                    const currentDeckHalfWidth = form.values.load?.deckHalfWidth;
                                    const currentDeckHalfLength = form.values.load?.deckHalfLength;
                                    const currentUniformLoad = form.values.load?.uniformLoad;

                                    if (!currentDeckHalfWidth || currentDeckHalfWidth === '' || currentDeckHalfWidth === 0) {
                                        form.setFieldValue('load.deckHalfWidth', 0.75);
                                    }
                                    if (!currentDeckHalfLength || currentDeckHalfLength === '' || currentDeckHalfLength === 0) {
                                        form.setFieldValue('load.deckHalfLength', 6.25);
                                    }
                                    if (!currentUniformLoad || currentUniformLoad === '' || currentUniformLoad === 0) {
                                        form.setFieldValue('load.uniformLoad', 80);
                                    }
                                } else if (newValue === 'Circular') {
                                    // Set defaults for Circular load fields if they don't already have values
                                    const currentWheelLoad = form.values.load?.wheelLoad;
                                    const currentDualSpacing = form.values.load?.dualSpacing;
                                    const currentDualTandemSpacing = form.values.load?.dualTandemSpacing;
                                    const currentTandemSpacing = form.values.load?.tandemSpacing;
                                    const currentTirePressure = form.values.load?.tirePressure;

                                    if (!currentWheelLoad || currentWheelLoad === '' || currentWheelLoad === 0) {
                                        form.setFieldValue('load.wheelLoad', 25000);
                                    }
                                    if (!currentDualSpacing || currentDualSpacing === '' || currentDualSpacing === 0) {
                                        form.setFieldValue('load.dualSpacing', 0.3);
                                    }
                                    if (!currentDualTandemSpacing || currentDualTandemSpacing === '' || currentDualTandemSpacing === 0) {
                                        form.setFieldValue('load.dualTandemSpacing', 1.2);
                                    }
                                    if (!currentTandemSpacing || currentTandemSpacing === '' || currentTandemSpacing === 0) {
                                        form.setFieldValue('load.tandemSpacing', 1.2);
                                    }
                                    if (!currentTirePressure || currentTirePressure === '' || currentTirePressure === 0) {
                                        form.setFieldValue('load.tirePressure', 0.7);
                                    }
                                } else if (newValue === 'Railway') {
                                    // Set defaults for Railway load fields if they don't already have values
                                    const currentDeckHalfWidth = form.values.load?.deckHalfWidth;
                                    const currentDeckHalfLength = form.values.load?.deckHalfLength;
                                    const currentUniformLoad = form.values.load?.uniformLoad;

                                    if (!currentDeckHalfWidth || currentDeckHalfWidth === '' || currentDeckHalfWidth === 0) {
                                        form.setFieldValue('load.deckHalfWidth', 0.48);
                                    }
                                    if (!currentDeckHalfLength || currentDeckHalfLength === '' || currentDeckHalfLength === 0) {
                                        form.setFieldValue('load.deckHalfLength', 0.15);
                                    }
                                    if (!currentUniformLoad || currentUniformLoad === '' || currentUniformLoad === 0) {
                                        form.setFieldValue('load.uniformLoad', 403);
                                    }
                                }
                            }
                        }
                    }}
                    size="small"
                    disabled={isDisabled}>
                    {typeFieldConfig.options?.map(option => (
                        <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', px: 2 }}>
                            {t(option.label)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}
        </Field>
        <ErrorMessage name={`${groupName}.${typeFieldConfig.name}`} render={renderErrorMessage} />
    </Box>
);

// Extract geogrid layer section component
const GeogridLayerSection: React.FC<{
    groupName: string;
    parentFieldConfigs: FieldConfig[];
    itemFieldConfigs: FieldConfig[];
    t: TFunction;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    productData: any;
}> = ({ groupName, parentFieldConfigs, itemFieldConfigs, t, getMeasurementUnit, productData }) => (
    <>
        <Box sx={{ padding: '0 16px 8px 16px' }}>
            {parentFieldConfigs.map(config =>
                renderSingleField({
                    fullFieldName: `${groupName}.${config.name}`,
                    fieldConfig: config,
                    t,
                    getMeasurementUnit,
                })
            )}
        </Box>
        <GeogridLayerManager
            groupName={groupName}
            itemFieldConfigs={itemFieldConfigs}
            t={t}
            getMeasurementUnit={getMeasurementUnit}
            renderErrorMessageComponent={renderErrorMessageComponent}
            productData={productData}
        />
    </>
);

// Extract form content to move useEffect outside of Formik render
interface GeoGridFormContentProps {
    setFieldValue: (field: string, value: unknown) => void;
    values: GeoGridFormData;
    dirty: boolean;
    groupedFields: Record<string, FieldConfig[]>;
    projectUnit: unitEnum;
    dispatch: ReturnType<typeof useAppDispatch>;
    location: ReturnType<typeof useLocation>;
    isGeogridTypeDisabled?: boolean;
    currentProjectType?: EProjectType;
    openAccordions: Record<string, boolean>;
    handleAccordionToggle: (groupName: string) => void;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    isNewProject: boolean;
    productData: any;
    t: TFunction;
    config: any | null; // Aggiungi config alle props
}

const GeoGridFormContent: React.FC<GeoGridFormContentProps> = ({
    setFieldValue,
    values,
    dirty,
    groupedFields,
    projectUnit,
    dispatch,
    location,
    isGeogridTypeDisabled,
    currentProjectType,
    openAccordions,
    handleAccordionToggle,
    getMeasurementUnit,
    isNewProject,
    productData,
    t,
    config, // Aggiungi config qui
}) => {
    // Use custom hook for graphic preview - MOVED OUTSIDE FORMIK RENDER
    useGraphicPreview(values, projectUnit, groupedFields, dispatch);

    // GeoGrid dirty check - similar to InputForm
    const geogridHasBeenRan = useAppSelector(state => state.Dynamic.project.data?.geogridHasBeenRan);
    const geogridUserModified = useAppSelector(state => state.Dynamic.project.data?.geogridUserModified);

    // Note: Debounced Redux updates removed to prevent form reloads
    // Redux is now only updated on form submission to maintain stability

    // FIXED: Simplified user modification tracking without constant Redux updates
    // This prevents form reloads while still tracking user modifications
    useEffect(() => {
        if (dirty && !geogridUserModified) {
            logDebug('GeoGridForm', 'Form is dirty, setting geogridUserModified flag', undefined, LogTag.FORM_VALIDATION);
            dispatch(setGeogridUserModified(true));
        }
    }, [dirty, geogridUserModified, dispatch]);

    useEffect(() => {
        if (dirty && geogridHasBeenRan === true) {
            logDebug('GeoGridForm', 'Dirty Check - Form is dirty and geogrid was run. Dispatching setGeogridHasBeenRan(false)', undefined, LogTag.GEOGRID_CALC);
            dispatch(setGeogridHasBeenRan(false));
        }
    }, [dirty, geogridHasBeenRan, dispatch]);

    // Handle geogridType auto-setting with cleanup - MOVED OUTSIDE FORMIK RENDER
    useEffect(() => {
        if (isGeogridTypeDisabled && currentProjectType) {
            let targetValue: string;
            if (currentProjectType === EProjectType.container_yard) {
                targetValue = 'Rectangular';
            } else if (currentProjectType === EProjectType.railways) {
                targetValue = 'Railway';
            } else {
                targetValue = 'Circular';
            }

            const currentValue = getIn(values, 'type.geogridType');
            if (currentValue !== targetValue) {
                setFieldValue('type.geogridType', targetValue);
            }
        }
    }, [isGeogridTypeDisabled, currentProjectType, setFieldValue, values]);

    // Auto-reset Surface Layer values to 0 when Railway is selected
    useEffect(() => {
        const currentGeogridType = getIn(values, 'type.geogridType');
        if (currentGeogridType === 'Railway') {
            // Force all surface layer values to 0 for Railway (Excel: no TOP COURSE)
            const surfaceThickness = getIn(values, 'surfaceLayer.thickness');
            const surfaceUnitWeight = getIn(values, 'surfaceLayer.unitWeight');
            const surfaceLoadSpreadingAngle = getIn(values, 'surfaceLayer.loadSpreadingAngle');

            if (surfaceThickness !== 0) {
                setFieldValue('surfaceLayer.thickness', 0);
            }
            if (surfaceUnitWeight !== 0) {
                setFieldValue('surfaceLayer.unitWeight', 0);
            }
            if (surfaceLoadSpreadingAngle !== 0) {
                setFieldValue('surfaceLayer.loadSpreadingAngle', 0);
            }
        }
    }, [getIn(values, 'type.geogridType'), setFieldValue, values]);

    // Function to check if a group has any visible fields
    const hasVisibleFields = (groupName: string, fieldConfigs: FieldConfig[]): boolean => {
        return fieldConfigs.some(fieldConfig => {
            // Check displayCondition
            if (fieldConfig.displayCondition) {
                const controllingFieldValue = getIn(values, fieldConfig.displayCondition.field);
                if (controllingFieldValue !== fieldConfig.displayCondition.value) {
                    return false;
                }
            }

            // Check hideWhen condition
            if (fieldConfig.hideWhen) {
                const controllingFieldValue = getIn(values, fieldConfig.hideWhen.field);
                if (controllingFieldValue === fieldConfig.hideWhen.value) {
                    return false;
                }
            }

            return true;
        });
    };

    return (
        <Form role="form" data-testid="geogrid-dynamic-form">
            {Object.keys(groupedFields)
                .filter(groupName => !groupName.endsWith('Item'))
                .filter(groupName => !config?.hiddenGroups?.includes(groupName)) // Nasconde i gruppi specificati in hiddenGroups
                .filter(groupName => hasVisibleFields(groupName, groupedFields[groupName])) // Nasconde i gruppi senza campi visibili
                .map(groupName => (
                    <AccordionSection
                        key={`geogrid-${groupName}`}
                        groupName={groupName}
                        parentFieldConfigs={groupedFields[groupName]}
                        itemFieldConfigs={groupedFields.geogridItem}
                        isOpen={!!openAccordions[groupName]}
                        onToggle={() => handleAccordionToggle(groupName)}
                        values={values}
                        setFieldValue={setFieldValue}
                        t={t}
                        getMeasurementUnit={getMeasurementUnit}
                        isNewProject={isNewProject}
                        productData={productData}
                        isGeogridTypeDisabled={isGeogridTypeDisabled}
                        currentProjectType={currentProjectType}
                        accordionTitles={config?.accordionTitles}
                    />
                ))}
        </Form>
    );
};

export default GeoGridForm;
